<template>
  <div class="rpilot-tsp-page-root">
    <!-- Step 1: chooser (full-width, no chat sidebar yet) -->
    <v-card v-if="currentStep === '1'" flat>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8" lg="6">
            <div class="d-flex align-center mb-1">
              <img :src="rpilotIcon" alt="" width="40" height="40" class="mr-3 flex-shrink-0" />
              <div class="text-h6">RPilot — Task Set Profile Assistant</div>
            </div>
            <div class="text-body-2 text-medium-emphasis mb-6">
              Pick what you'd like to do, then start the chat. RPilot can build a new
              task set profile from scratch or apply bulk updates to an existing one.
            </div>

            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <v-card
                  link
                  variant="outlined"
                  class="rpilot-mode-card"
                  :class="{ 'rpilot-mode-card--selected': mode === 'create' }"
                  @click="mode = 'create'"
                >
                  <v-card-text class="d-flex align-center">
                    <v-icon
                      size="28"
                      class="mr-3"
                      :color="mode === 'create' ? 'primary' : undefined"
                    >
                      mdi-playlist-plus
                    </v-icon>
                    <div>
                      <div class="text-subtitle-2">Create new profile</div>
                      <div class="text-caption text-medium-emphasis">
                        Build a task set profile from scratch in chat
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card
                  link
                  variant="outlined"
                  class="rpilot-mode-card"
                  :class="{ 'rpilot-mode-card--selected': mode === 'update' }"
                  @click="mode = 'update'"
                >
                  <v-card-text class="d-flex align-center">
                    <v-icon
                      size="28"
                      class="mr-3"
                      :color="mode === 'update' ? 'primary' : undefined"
                    >
                      mdi-playlist-edit
                    </v-icon>
                    <div>
                      <div class="text-subtitle-2">Update existing profile</div>
                      <div class="text-caption text-medium-emphasis">
                        Apply bulk changes to a profile you pick
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-if="mode === 'update'">
                <v-autocomplete
                  v-model="selectedEntry"
                  :items="sortedProfiles"
                  item-title="title"
                  :return-object="true"
                  label="Task set profile"
                  variant="outlined"
                  density="comfortable"
                  :loading="loadingProfiles"
                  clearable
                  class="mt-2"
                />
              </div>
            </v-expand-transition>

            <v-btn color="primary" class="mt-2" :disabled="!canProceed" @click="onNext">
              Next
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Step 2: preview + chat sidebar split -->
    <div v-else class="rpilot-tsp-page">
      <div class="rpilot-tsp-main elevation-2">
        <RPilotTspPreview
          ref="previewRef"
          :preview-tree="preview.tree"
          :subtask-map="preview.subtaskMap"
          :counts="preview.counts"
          :profile-title="displayProfileTitle"
          :profile-code="displayProfileCode"
          :is-create="isCreateProposal"
          :has-proposal="!!pendingProposal"
          :saving="saving"
          :publish-requested="pendingProposal?.publish || false"
          :publish-message="pendingProposal?.publishMessage || ''"
          @save="onApply"
          @back="goBack"
          @update:profile-code="onEditIdentity('code', $event)"
          @update:profile-title="onEditIdentity('title', $event)"
        />

        <v-alert
          v-if="pendingProposal"
          type="info"
          variant="tonal"
          density="compact"
          class="rpilot-tsp-proposal-banner"
        >
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <strong>Proposal from RPilot:</strong>
              {{ pendingProposal.summary || 'Untitled change' }}
              <span class="text-medium-emphasis ml-2">
                ({{ preview.counts.new }} new, {{ preview.counts.updated }} updated,
                {{ preview.counts.deleted }} deleted{{ subtaskChangeSuffix }})
              </span>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              class="mr-1"
              @click="openConfirmDialog"
            >
              Save…
            </v-btn>
            <v-btn variant="text" size="small" @click="rejectProposal">Reject</v-btn>
          </div>
        </v-alert>
      </div>

      <RPilotChatSidebar
        ref="chatRef"
        class="elevation-2"
        :scoped-token="settingsStore.settings.scopedToken"
        :api-url="settingsStore.settings.apiUrl"
        :project-code="settingsStore.settings.projectCode"
        :session-context="sessionContext"
        :features="{ audio: true, files: true, images: false, thinking: true, sessions: true }"
        persist-session
        @tool-call="onToolCall"
        @tool-call-cancelled="resetSession"
        @new-chat="resetSession"
      />
    </div>

    <RPilotDeploymentDialog
      v-model="deploymentDialog"
      :profile-code="pendingDeployment?.published?.code || ''"
      :profile-title="pendingDeployment?.published?.title || ''"
      :schedule-title="pendingDeployment?.scheduleTitle || ''"
      :targets="pendingDeployment?.targets || []"
      :title="pendingDeployment?.title || ''"
      :message="pendingDeployment?.message || ''"
      :deploying="deploying"
      @confirm="onConfirmDeployment"
      @cancel="cancelDeployment"
    />

    <v-snackbar v-model="showSuccessSnackbar" :timeout="3000" color="success">
      {{ successMessage }}
    </v-snackbar>
    <v-snackbar v-model="showErrorSnackbar" :timeout="5000" color="error">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useSettingsStore } from '@/stores/settings';
import { useAppStore } from '@/stores/app';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useRPilotStore } from '@/stores/rpilot';
import RPilotTspPreview from '@/components/steps/RPilotTspPreview.vue';
import RPilotDeploymentDialog from '@/components/dialogs/RPilotDeploymentDialog.vue';
import RPilotChatSidebar from '@/components/RPilotChatSidebar.vue';
import {
  buildProposalPreview,
  hydrateProposedTaskProfiles,
  validateProposedTaskProfiles
} from '@/utils/TaskSetProfileProposalDiff';
import { prepareDeploymentProposal, describeApiError } from '@/utils/TaskSetProfileDeploymentProposal';
import { flattenTasks } from '@/utils/TaskSetProfileChangeDetector';
import { useChatStore } from '@rpilot/chat/stores/chat.js';
import rpilotIcon from '@/assets/rpilot-icon.svg';

const settingsStore = useSettingsStore();
const appStore = useAppStore();
const scheduleStore = useScheduleStore();
const taskStore = useTaskStore();
const rpilotStore = useRPilotStore();
const router = useRouter();

const currentStep = ref('1');
const mode = ref('create');
const selectedEntry = ref(null);      // { title, code, profile } from the chooser
const loadingProfiles = ref(false);
const saving = ref(false);

// allProfiles mirrors TaskSetProfileUpdatePage's list: every profile (drafts
// included), used both for the chooser and to resolve proposal targets and
// detect code collisions on create.
const allProfiles = ref([]);
const teams = ref([]);

// The live profile the proposal diffs against (null on create-from-scratch)
// and the agent's proposed replacement ({ code, title, taskProfiles } | null).
const existingProfile = ref(null);
const proposedProfile = ref(null);

// When set, the agent is awaiting our respond() call.
// Apply path: user confirms in the preview → onApply → REST write + respond.
// Reject path: user clicks Reject in the banner → respond with error and stay
// in the chat so the conversation continues.
const pendingProposal = ref(null);    // { taskSetProfileId, isCreate, summary, publish, publishMessage, respond }

// Pending deployment proposal — separate from pendingProposal so an in-flight
// profile commit isn't clobbered by a deployment (or vice versa).
const pendingDeployment = ref(null);  // { published, targets, scheduleId, scheduleTitle, title, message, respond }
const deploymentDialog = ref(false);
const deploying = ref(false);

const chatRef = ref(null);
const previewRef = ref(null);
// Banner Save… opens the preview's confirm dialog — same dialog, same publish
// toggle, as the action-row Create/Update Profile button (mirrors the
// schedules page's openCommitDialog).
const openConfirmDialog = () => previewRef.value?.openConfirmDialog();

const showSuccessSnackbar = ref(false);
const successMessage = ref('');
const showErrorSnackbar = ref(false);
const errorMessage = ref('');

const sortedProfiles = computed(() =>
  [...allProfiles.value]
    .filter((entry) => entry.profile?.current !== false)
    .sort((a, b) => (a.profile?.title || '').localeCompare(b.profile?.title || ''))
);

const canProceed = computed(() => {
  if (mode.value === 'create') return true;
  return !!selectedEntry.value;
});

// Name resolvers for the subtask dialog rows.
const teamMap = computed(() => new Map(teams.value.map((t) => [t.id, t.name])));
const templateMap = computed(
  () => new Map((taskStore.itemTemplates || []).map((t) => [t.code, t.title]))
);

// Preview rows for the TreeGrid. Before any proposal arrives this is the
// baseline: the existing tree diffed against itself (all unchanged), or an
// empty tree on create — the preview component shows a hint instead.
const preview = computed(() => {
  const existingTree = existingProfile.value?.taskProfiles || [];
  const proposedTree = proposedProfile.value?.taskProfiles ?? existingTree;
  return buildProposalPreview(proposedTree, existingTree, {
    teamMap: teamMap.value,
    templateMap: templateMap.value
  });
});

const isCreateProposal = computed(() =>
  pendingProposal.value ? pendingProposal.value.isCreate : !existingProfile.value
);

const displayProfileCode = computed(
  () => proposedProfile.value?.code || existingProfile.value?.code || ''
);
const displayProfileTitle = computed(
  () => proposedProfile.value?.title || existingProfile.value?.title || ''
);

const subtaskChangeSuffix = computed(() => {
  const c = preview.value.counts;
  const total = (c.subtasksNew || 0) + (c.subtasksUpdated || 0) + (c.subtasksDeleted || 0);
  return total > 0 ? `, ${total} subtask change${total !== 1 ? 's' : ''}` : '';
});

const sessionContext = computed(() => {
  if (currentStep.value !== '2') return null;
  const projectName = settingsStore.settings.projectName || null;
  const projectCode = settingsStore.settings.projectCode || null;
  if (mode.value === 'create') {
    return { mode: 'tsp-create', projectName, projectCode };
  }
  const profile = selectedEntry.value?.profile;
  if (!profile) return { mode: 'tsp-update', projectName, projectCode };
  return {
    mode: 'tsp-update',
    projectName,
    projectCode,
    taskSetProfile: { id: profile.id, code: profile.code, title: profile.title }
  };
});

const loadProfiles = async () => {
  loadingProfiles.value = true;
  try {
    const projectCode = settingsStore.settings.projectCode;
    const [profiles, teamsData] = await Promise.all([
      appStore.apiClient.get(`/api/${projectCode}/task-set-profiles`),
      appStore.apiClient.get(`/api/${projectCode}/teams`)
    ]);
    allProfiles.value = (profiles || []).map((p) => ({
      title: `${p.code} | ${p.title}`,
      code: p.code,
      profile: p
    }));
    teams.value = teamsData || [];
  } catch (err) {
    console.error('[rpilot-tsp] failed to load task set profiles', err);
    errorMessage.value = 'Failed to load task set profiles';
    showErrorSnackbar.value = true;
  } finally {
    loadingProfiles.value = false;
  }
};

const onNext = () => {
  existingProfile.value = mode.value === 'update' ? selectedEntry.value?.profile || null : null;
  proposedProfile.value = null;
  pendingProposal.value = null;
  currentStep.value = '2';
  // No auto-prompt — the user starts the conversation. Context (mode, profile
  // identity, project) flows to the agent via the sessionContext prop.
};

const goBack = () => {
  currentStep.value = '1';
  if (pendingProposal.value) rejectProposal();
  if (pendingDeployment.value) cancelDeployment();
};

// New chat: settle any dangling proposal (the abandoned session can never
// answer it, and rejectProposal also rolls the preview back to baseline), but
// stay where the user is — a new conversation, not a new workflow. Mode and
// selected profile are kept; the next session picks them up via the
// sessionContext prop, and the Back button remains the way to return to the
// chooser.
const resetSession = () => {
  if (pendingProposal.value) rejectProposal();
  if (pendingDeployment.value) cancelDeployment();
};

// ── Frontend-tool dispatch ───────────────────────────────────────────────

const onToolCall = ({ name, args, respond, setStatus }) => {
  if (name === 'rdrive_propose_task_set_profile_commit') {
    handleProposeTspCommit(args, respond, setStatus);
    return;
  }
  if (name === 'rdrive_propose_task_set_profile_deployment') {
    handleProposeDeployment(args, respond, setStatus);
    return;
  }
  if (name === 'rdrive_switch_view') {
    handleSwitchView(args, respond);
    return;
  }
  if (name === 'rdrive_propose_schedule_commit') {
    respond({
      error:
        'This RPilot view manages task set profiles, not schedules — the proposal was not shown. ' +
        'Call rdrive_switch_view({ view: "schedules" }) to move the dashboard to the Schedules ' +
        'view, then re-propose there.'
    });
    return;
  }
  respond({ error: `Unknown frontend tool: ${name}` });
};

// Agent-driven navigation to the Schedules RPilot view. Respond BEFORE
// navigating: the respond closure and the chat stores outlive this page
// (shared Pinia), so the resumed stream keeps flowing while the other page
// mounts and — via persist-session — picks the transcript straight up.
const handleSwitchView = (args, respond) => {
  const view = args?.view;
  if (view === 'task-set-profiles') {
    respond({ result: JSON.stringify({ switched: false, view, note: 'Already on the Task Set Profiles view.' }) });
    return;
  }
  if (view !== 'schedules') {
    respond({ error: `Unknown view "${view}" — use "schedules" or "task-set-profiles".` });
    return;
  }
  // An unanswered proposal would hang the agent forever once this page
  // unmounts — settle it first.
  if (pendingProposal.value || pendingDeployment.value) {
    respond({
      error: 'A proposal on the Task Set Profiles view is still awaiting the user — ' +
        'wait for its outcome (or ask the user to resolve it) before switching views.'
    });
    return;
  }
  rpilotStore.setHandoff({
    view,
    mode: args?.mode || null,
    scheduleId: args?.scheduleId || null,
    taskSetProfileId: null,
  });
  respond({ result: JSON.stringify({ switched: true, view }) });
  router.push({ name: 'RPilot' });
};

// Reject a proposal back to the agent AND surface why in the snackbar — a
// silent respond({ error }) leaves the user staring at an unchanged preview
// with no clue the proposal was rejected.
const rejectProposalToAgent = (respond, message) => {
  errorMessage.value = `RPilot proposal rejected: ${message}`;
  showErrorSnackbar.value = true;
  respond({ error: message });
};

const handleProposeTspCommit = async (args, respond, setStatus) => {
  // The harness inlines taskProfilesHandle into taskProfiles before the call
  // reaches us. If the handle survives, the server predates that expansion.
  if (args?.taskProfilesHandle && !Array.isArray(args?.taskProfiles)) {
    rejectProposalToAgent(
      respond,
      'taskProfilesHandle was not expanded by the RPilot server (harness out of date). ' +
        'Propose with inline taskProfiles for now.'
    );
    return;
  }
  const validationErrors = validateProposedTaskProfiles(args?.taskProfiles);
  if (validationErrors.length > 0) {
    rejectProposalToAgent(respond, `Invalid taskProfiles: ${validationErrors.join('; ')}`);
    return;
  }

  // Re-fetch before resolving the target: the page's list may predate profile
  // deletions/creations made elsewhere (RDrive, other tabs), and a stale list
  // here produces false code clashes or false missing-id errors.
  await loadProfiles();

  let targetEntry = null;
  if (args?.taskSetProfileId) {
    targetEntry = allProfiles.value.find((e) => e.profile?.id === args.taskSetProfileId) || null;
    if (!targetEntry) {
      rejectProposalToAgent(respond, `No task set profile found with id ${args.taskSetProfileId}`);
      return;
    }
    // A published revision is immutable, but that's a storage detail the
    // agent shouldn't have to care about: accept the update proposal and, on
    // apply, POST a new revision with the same code instead of PUTting the
    // published row (the list is latest-per-code, so a listed published row
    // means no draft exists to collide with).
  } else {
    if (!args?.newProfile?.title || !args?.newProfile?.code) {
      rejectProposalToAgent(
        respond,
        'Provide taskSetProfileId to update an existing profile, or newProfile { title, code } to create one.'
      );
      return;
    }
    const clash = allProfiles.value.find((e) => e.code === args.newProfile.code);
    if (clash) {
      rejectProposalToAgent(
        respond,
        `A task set profile with code "${args.newProfile.code}" already exists (${clash.profile.title}). Use taskSetProfileId "${clash.profile.id}" to update it, or pick a different code.`
      );
      return;
    }
  }

  existingProfile.value = targetEntry?.profile || null;
  proposedProfile.value = {
    code: targetEntry ? targetEntry.profile.code : args.newProfile.code,
    title: args?.title || args?.newProfile?.title || targetEntry?.profile?.title || '',
    // The API validates versionReferences (non-empty, unique) but never
    // generates them, so mint them here — before the preview is built, so
    // the diff the user approves is exactly the payload that gets saved.
    taskProfiles: hydrateProposedTaskProfiles(
      args.taskProfiles,
      targetEntry?.profile?.taskProfiles || []
    )
  };

  // Updating a published revision saves as a NEW revision of the same code
  // rather than mutating the immutable published row. Publish defaults ON for
  // that case (unless the agent explicitly opted out): the point of profiles
  // is centrally-managed repeating structure, so an update to a published
  // profile should sync into its deployed instances — publishing the new
  // revision is what triggers that sync. The user still has the final say via
  // the confirm dialog's Publish toggle.
  const targetIsPublished = targetEntry?.profile?.isPublished === true;
  pendingProposal.value = {
    taskSetProfileId: targetEntry?.profile?.id || null,
    isCreate: !targetEntry,
    isNewRevision: targetIsPublished,
    summary: args?.summary || '',
    publish: args?.publish === true || (targetIsPublished && args?.publish !== false),
    publishMessage: args?.publishMessage || '',
    respond
  };

  // The chat's loader keeps running while the turn waits on the user — tell
  // it what it is waiting for instead of the tool's "Proposing…" summary.
  setStatus?.('Review the proposal in the preview, then Save or Reject');
  postProposalSummaryToChat();
};

// Push a summary of the incoming proposal into the chat transcript — same
// rationale as the schedules RPilot page: the model doesn't reliably write
// prose alongside the tool call, so build a deterministic summary here.
const postProposalSummaryToChat = () => {
  const chatStore = useChatStore();
  const proposal = pendingProposal.value;
  const counts = preview.value.counts;

  const heading = proposal.isCreate
    ? `**Proposal — new profile "${proposedProfile.value.title}"** (code \`${proposedProfile.value.code}\`)`
    : `**Proposal — ${proposedProfile.value.code} | ${proposedProfile.value.title}**`;

  const lines = [heading];
  if (proposal.summary) lines.push(proposal.summary);
  if (proposal.isNewRevision) {
    lines.push(
      'The current revision is published, so saving will create a **new revision** of the profile.'
    );
  }
  if (proposal.publish) {
    lines.push(
      `Will be **published** on save${proposal.publishMessage ? ` (publish message: ${proposal.publishMessage})` : ''}` +
      (proposal.isNewRevision ? ' — publishing syncs the changes into every schedule this profile is deployed to.' : '.')
    );
  }
  lines.push(
    `${counts.new} new · ${counts.updated} updated · ${counts.deleted} deleted${subtaskChangeSuffix.value} — ` +
    `review the highlighted preview, then ${proposal.isCreate ? 'Create' : 'Update'} Profile to apply or Reject.`
  );
  chatStore.messages.push({ role: 'assistant', content: lines.join('\n\n') });
};

// Triggered by the preview's confirm dialog. The dialog's publish toggle wins
// over the agent's requested publish flag — the user has the final say.
const onApply = async ({ publish = false, publishMessage = '' } = {}) => {
  const proposal = pendingProposal.value;
  if (!proposal) return;

  saving.value = true;
  const projectCode = settingsStore.settings.projectCode;
  try {
    let profileId = proposal.taskSetProfileId;

    if (proposal.isCreate || proposal.isNewRevision) {
      // Create and new-revision share the POST path (mirrors
      // TaskSetProfileCreatePage's confirmImport payload). For an update
      // targeting a published, immutable revision, POSTing the same code with
      // an empty id is the API's new-revision path: the server mints a fresh
      // id/key and the row becomes the family's current revision.
      // versionReferences were preserved from the published tree at hydration
      // time, so the publish step below syncs into deployed schedules.
      const created = await appStore.apiClient.post(
        `/api/${projectCode}/task-set-profiles`,
        {
          code: proposedProfile.value.code,
          title: proposedProfile.value.title,
          taskProfiles: proposedProfile.value.taskProfiles,
          key: '',
          isPublished: false,
          current: true,
          publishMessage: ''
        }
      );
      profileId = created?.id || null;
    } else {
      // Mirrors TaskSetProfileUpdatePage's handleConfirmUpdate payload:
      // preserve API-managed fields from the existing profile.
      const existing = existingProfile.value;
      await appStore.apiClient.put(
        `/api/${projectCode}/task-set-profiles/${existing.id}`,
        {
          code: proposedProfile.value.code,
          title: proposedProfile.value.title,
          taskProfiles: proposedProfile.value.taskProfiles,
          id: existing.id,
          key: existing.key,
          isPublished: existing.isPublished,
          publishMessage: existing.publishMessage
        }
      );
    }

    // Re-baseline against the server's copy so the next proposal diffs
    // against exactly what was saved (versionReferences are minted client-side
    // at proposal time — the server only validates them). The create POST may
    // not return the new id; the code is unique, so fall back to it.
    await loadProfiles();
    const resolveFreshEntry = () =>
      allProfiles.value.find((e) => e.profile?.id === profileId) ||
      allProfiles.value.find((e) => e.code === proposedProfile.value.code) ||
      null;
    let freshEntry = resolveFreshEntry();

    // Publish is a separate PUT of the server's own copy with the flag
    // flipped (mirrors the RDrive SPA's publish dialog) — the create POST is
    // not guaranteed to accept isPublished: true.
    let publishError = null;
    if (publish && freshEntry) {
      try {
        await appStore.apiClient.put(
          `/api/${projectCode}/task-set-profiles/${freshEntry.profile.id}`,
          { ...freshEntry.profile, isPublished: true, publishMessage }
        );
        profileId = freshEntry.profile.id;
        await loadProfiles();
        freshEntry = resolveFreshEntry();
      } catch (err) {
        console.error('[rpilot-tsp] publish failed', err);
        publishError = err.message || String(err);
      }
    } else if (publish && !freshEntry) {
      publishError = 'Saved profile could not be re-fetched to publish it';
    }

    const taskCount = flattenTasks(proposedProfile.value.taskProfiles).length;
    // A failed publish after a successful save is still applied: true — the
    // agent should re-request publish, not re-propose the whole tree.
    proposal.respond({
      result: JSON.stringify({
        applied: true,
        taskSetProfileId: freshEntry?.profile?.id || profileId,
        code: proposedProfile.value.code,
        taskCount,
        isPublished: freshEntry?.profile?.isPublished === true,
        // The published revision was left untouched; the save created a new
        // revision (now current) under the same code.
        ...(proposal.isNewRevision
          ? { newRevision: true, previousRevisionId: proposal.taskSetProfileId }
          : {}),
        ...(publishError ? { publishError } : {})
      })
    });
    pendingProposal.value = null;

    if (freshEntry) {
      mode.value = 'update';
      selectedEntry.value = freshEntry;
      existingProfile.value = freshEntry.profile;
    }
    proposedProfile.value = null;

    if (publishError) {
      errorMessage.value = `Profile saved but publish failed: ${publishError}`;
      showErrorSnackbar.value = true;
    } else {
      const publishedSuffix = publish ? ' (published)' : '';
      successMessage.value = (proposal.isCreate
        ? `Created profile ${freshEntry?.profile?.code || ''}`.trim()
        : proposal.isNewRevision
          ? `Updated profile ${freshEntry?.profile?.code || ''} (new revision)`.trim()
          : `Updated profile ${freshEntry?.profile?.code || ''}`.trim()) + publishedSuffix;
      showSuccessSnackbar.value = true;
    }
  } catch (err) {
    console.error('[rpilot-tsp] apply failed', err);
    proposal.respond({ error: `Apply failed: ${err.message || err}` });
    pendingProposal.value = null;
    errorMessage.value = `Failed to ${proposal.isCreate ? 'create' : 'update'} profile: ${err.message || err}`;
    showErrorSnackbar.value = true;
  } finally {
    // The proposal is settled either way (applied, or bounced back to the
    // agent), so the confirm dialog has nothing left to confirm.
    saving.value = false;
    previewRef.value?.closeConfirmDialog();
  }
};

// Double-click edits of the header code/title on a create proposal. The
// proposed profile is the payload onApply saves, so updating it here is all
// that's needed — the preview header and confirm dialog read from it.
const onEditIdentity = (field, value) => {
  if (!proposedProfile.value || !pendingProposal.value?.isCreate) return;
  // Same clash rule the proposal itself was validated against — catch it here
  // rather than as a server error at save time.
  if (field === 'code') {
    const clash = allProfiles.value.find((e) => e.code === value);
    if (clash) {
      errorMessage.value = `A task set profile with code "${value}" already exists (${clash.profile.title})`;
      showErrorSnackbar.value = true;
      return;
    }
  }
  proposedProfile.value = { ...proposedProfile.value, [field]: value };
};

const rejectProposal = () => {
  if (!pendingProposal.value) return;
  pendingProposal.value.respond({ error: 'User rejected the proposed changes' });
  pendingProposal.value = null;
  // Roll the preview back to what the chooser selected — a rejected proposal
  // may have retargeted existingProfile to a different profile.
  proposedProfile.value = null;
  existingProfile.value = mode.value === 'update' ? selectedEntry.value?.profile || null : null;
};

// ── Task set profile deployment proposals ────────────────────────────────

const handleProposeDeployment = async (args, respond, setStatus) => {
  if (pendingDeployment.value) {
    respond({
      error: 'A deployment proposal is already awaiting the user — wait for its outcome before proposing another.'
    });
    return;
  }
  try {
    const result = await prepareDeploymentProposal(args, {
      apiClient: appStore.apiClient,
      projectCode: settingsStore.settings.projectCode
    });
    if (result.error) {
      respond({ error: result.error });
      return;
    }

    const schedule = (scheduleStore.schedules || []).find((s) => s.id === args.scheduleId) || null;

    pendingDeployment.value = {
      published: result.published,
      targets: result.targets,
      scheduleId: args.scheduleId,
      scheduleTitle: schedule?.title || args.scheduleId,
      title: args?.title || '',
      message: args?.message || '',
      respond
    };
    deploymentDialog.value = true;
    setStatus?.('Confirm or cancel the deployment');

    const chatStore = useChatStore();
    chatStore.messages.push({
      role: 'assistant',
      content:
        `**Deployment proposal — ${result.published.code} | ${result.published.title}**\n\n` +
        `Deploy under ${result.targets.length} leaf task${result.targets.length !== 1 ? 's' : ''} in ` +
        `${schedule?.title || args.scheduleId} — Confirm Deploy to apply or Cancel.`
    });
  } catch (err) {
    console.error('[rpilot-tsp] deployment proposal failed', err);
    respond({ error: `Deployment proposal failed: ${err.message || err}` });
  }
};

const onConfirmDeployment = async ({ title, message }) => {
  const deployment = pendingDeployment.value;
  if (!deployment) return;

  deploying.value = true;
  try {
    await appStore.apiClient.patch(
      `/api/${settingsStore.settings.projectCode}/task-set-profiles/${deployment.published.id}/deployments`,
      {
        title,
        message,
        // id: '' matches the proven ScheduleImporter payload shape.
        deployments: deployment.targets.map((t) => ({ id: '', parentTaskId: t.id }))
      }
    );
    deployment.respond({
      result: JSON.stringify({
        applied: true,
        taskSetProfileId: deployment.published.id,
        profileCode: deployment.published.code,
        scheduleId: deployment.scheduleId,
        deployedCount: deployment.targets.length
      })
    });
    pendingDeployment.value = null;
    deploymentDialog.value = false;
    await loadProfiles();
    successMessage.value = `Deployed ${deployment.published.code} to ${deployment.targets.length} task${deployment.targets.length !== 1 ? 's' : ''}`;
    showSuccessSnackbar.value = true;
  } catch (err) {
    console.error('[rpilot-tsp] deployment failed', err);
    const reason = describeApiError(err);
    deployment.respond({ error: `Deployment failed: ${reason}` });
    pendingDeployment.value = null;
    deploymentDialog.value = false;
    errorMessage.value = `Deployment failed: ${reason}`;
    showErrorSnackbar.value = true;
  } finally {
    deploying.value = false;
  }
};

const cancelDeployment = () => {
  if (!pendingDeployment.value || deploying.value) return;
  pendingDeployment.value.respond({ error: 'User rejected the proposed deployment' });
  pendingDeployment.value = null;
  deploymentDialog.value = false;
};

// Agent-driven arrival via rdrive_switch_view from the Schedules view: consume
// the handoff and land directly in the chat view, so the session (carried
// across the navigation by persist-session) continues without the user
// re-doing the chooser. An unknown taskSetProfileId falls back to create mode —
// the agent learns the profile wasn't found from its next search, and the user
// still gets a working chat rather than a dead chooser.
onMounted(async () => {
  await loadProfiles();
  const handoff = rpilotStore.takeHandoff('task-set-profiles');
  if (!handoff) return;
  const entry = handoff.taskSetProfileId
    ? allProfiles.value.find((e) => e.profile?.id === handoff.taskSetProfileId) || null
    : null;
  if (entry) {
    mode.value = 'update';
    selectedEntry.value = entry;
  } else {
    mode.value = 'create';
    selectedEntry.value = null;
  }
  onNext();
});
</script>

<style scoped>
/* Step-1 mode chooser cards. Constant 2px border so selecting doesn't shift
   the layout; selected state recolors it and tints the background. */
.rpilot-mode-card {
  height: 100%;
  border-width: 2px;
  transition: border-color 0.15s, background-color 0.15s;
}
.rpilot-mode-card--selected {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.rpilot-tsp-page {
  display: flex;
  height: calc(100vh - 56px - 32px); /* viewport minus app bar minus content padding */
  min-height: 480px;
  gap: 12px;
}
.rpilot-tsp-main {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: auto;
  border-radius: 8px;
}
.rpilot-tsp-main > :first-child {
  flex: 1 1 auto;
  min-height: 0;
}
.rpilot-tsp-proposal-banner {
  position: sticky;
  bottom: 0;
  margin: 0;
  border-radius: 0;
  z-index: 2;
}
</style>
